/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-custom-classname */
'use client';

import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PageContentContainer } from '../../../PageContentContainer';
import { FeCarousel } from '../../components-internal/FeCarousel';
import { tr } from '../../helpers';
import { pushDataLayer } from '../../pushDataLayer';
import {
  ComponentHPArticlesModule,
  HpArticleItem,
} from '$/(cms)/_modules/cmsService/types';
import { getCmsPageLink } from '$/(cms)/_modules/routing';
import { FeLink } from '$/components/FeLink';
import { useClientContext } from '$/modules/context/client';

function removeTags(html: string): string {
  return typeof html === 'string' ? html.replace(/<\/?[^>]+(>|$)/g, '') : '';
}

function truncateText(text: string, maxLength = 60): string {
  return text && text.length > maxLength
    ? `${text.substring(0, maxLength)}...`
    : text;
}

export const ArticlesHPModule = ({
  title,
  articles: preArticles,
}: ComponentHPArticlesModule) => {
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const { brand, lang, langPrefix } = useClientContext();

  useEffect(() => {
    setWindowWidth(window.innerWidth);
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (contentRef.current) {
        setContainerHeight(contentRef.current.offsetHeight);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const articles: HpArticleItem[] = useMemo(() => {
    if (!preArticles?.length) {
      return [];
    }
    const minCount = 3;
    if (preArticles.length < minCount && windowWidth >= 1279) {
      return [
        ...preArticles,
        ...Array(minCount - preArticles.length).fill(null),
      ];
    }

    return preArticles;
  }, [preArticles, windowWidth]);

  useEffect(() => {
    if (contentRef.current) {
      setContainerHeight(contentRef.current.offsetHeight);
    }
  }, [articles, windowWidth]);

  const isSliderView = useMemo(
    () => windowWidth < 768 || windowWidth >= 1279,
    [windowWidth],
  );

  const isSwiperEnabled = useMemo(() => {
    const isMobileENABLED = windowWidth < 1279 && (articles?.length ?? 0) >= 2;
    const isDesktopENABLED =
      windowWidth >= 1279 && (articles?.length ?? 0) >= 4;

    return isMobileENABLED || isDesktopENABLED;
  }, [articles?.length, windowWidth]);

  if (!articles?.length) return null;

  return (
    <div className="relative" style={{ height: containerHeight || 'auto' }}>
      <div
        ref={contentRef}
        className={classNames(
          'inset absolute left-[calc(-50vw+50%)] z-0  w-screen md:w-[calc(100vw-8px)]',
          'bg-gradient-to-b from-[#E0F4F8] to-[#F5F5F5]',
          'flex justify-center px-2 pb-0 pt-4 sm:px-4 md:px-7 md:pt-10 lg:px-8 lg:pb-4',
        )}
      >
        <div className={classNames('max-w-screen ')}>
          <div
            className={classNames(
              'py-spacing/padding/soft-8px',
              // pageContentWidth.px,
            )}
          >
            <PageContentContainer>
              <h3 className="mb-4 text-left font-bold text-2xl sm:mb-6 sm:text-3xl md:mb-7 md:text-center md:text-4xl lg:mb-8">
                {title}
              </h3>

              {isSliderView ? (
                <FeCarousel
                  swiperPropsOverride={{
                    speed: 500,
                    enabled: isSwiperEnabled,
                    centeredSlides: false,
                    autoplay: {
                      delay: 2500,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    },
                    pagination: isSwiperEnabled ? { clickable: true } : false,
                    loop: windowWidth < 768,
                    breakpoints: {
                      360: { slidesPerView: 1 },
                      767: { slidesPerView: 1 },
                      1279: { slidesPerView: 3, spaceBetween: 24 },
                      1440: { slidesPerView: 3, spaceBetween: 24 },
                      1920: { slidesPerView: 3, spaceBetween: 24 },
                    },
                  }}
                  slideClassNames="flex flex-col"
                  showNavigationButton
                  navigationWrapperClassNames="hidden"
                >
                  {articles.map((item, index) => {
                    if (!item) {
                      // eslint-disable-next-line react/no-array-index-key
                      return <div key={`null-${index}`} />;
                    }
                    const link = getCmsPageLink({
                      brand,
                      pageType: 'article',
                      langPrefix,
                      slug: item.slug,
                      vertical: item.section ?? 'CREDIT_CARD',
                      urlPrefix: item.urlPrefix,
                    });
                    const img = item.image?.[0]?.url;
                    return (
                      <div
                        key={item.id}
                        className="flex h-full justify-center rounded-2xl p-2 pb-8 sm:rounded-3xl md:pb-2"
                      >
                        <div className="w-full rounded-2xl border border-gray-300 shadow-md transition bg-white hover:shadow-lg sm:rounded-3xl">
                          <FeLink
                            href={link}
                            target={item.id ? '_self' : '_blank'}
                            rel="noreferrer"
                          >
                            <CmsImage
                              unoptimized
                              src={img as string}
                              width={100}
                              height={100}
                              alt={item.title}
                              className="h-42 w-full rounded-t-2xl object-cover sm:h-[210px] md:h-60"
                            />
                          </FeLink>
                          <div className="flex min-h-[210px] flex-col px-6 py-4">
                            <h3 className="mb-2 font-bold text-xl">
                              <FeLink
                                href={link}
                                target={item.id ? '_self' : '_blank'}
                                rel="noreferrer"
                              >
                                {item.title}
                              </FeLink>
                            </h3>
                            <p className="text-gray-600 text-base">
                              {truncateText(removeTags(item.subtitle), 67)}
                            </p>
                            <a
                              href={link}
                              title={item.title}
                              target="_blank"
                              className="mt-auto flex w-full cursor-pointer items-center justify-center rounded-xl border border-primary py-2 transition text-primary hover:bg-primary-dark hover:text-white"
                              onClick={() =>
                                pushDataLayer({
                                  event: 'navigate',
                                  component_type: 'article-list',
                                  component_title: `${title}_${item.title}`,
                                  action: 'click',
                                  index,
                                })
                              }
                              rel="noreferrer"
                            >
                              <span>{tr('read_more', lang)}</span>
                              <MaterialIcon
                                className="ml-1"
                                icon="arrow_forward"
                                px={18}
                              />
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </FeCarousel>
              ) : (
                <div className="space-y-4">
                  {articles.map((item, index) => {
                    const linkToPage = getCmsPageLink({
                      brand,
                      pageType: 'article',
                      langPrefix,
                      slug: item.slug,
                      vertical: item.section ?? 'CREDIT_CARD',
                      urlPrefix: item.urlPrefix,
                    });
                    return (
                      <div
                        key={item.id}
                        className="flex overflow-hidden rounded-2xl border border-gray-300 bg-white"
                      >
                        <div className="w-1/3">
                          <a
                            href={linkToPage}
                            target={item.id ? '_self' : '_blank'}
                            rel="noreferrer"
                          >
                            <CmsImage
                              unoptimized
                              src={item.image?.[0]?.url as string}
                              width={100}
                              height={100}
                              alt={item.title}
                              className="size-full object-cover"
                            />
                          </a>
                        </div>
                        <div className="flex flex-1 flex-col justify-between p-6">
                          <h3 className="mb-2 font-semibold text-xl">
                            <FeLink
                              href={linkToPage}
                              target={item.id ? '_self' : '_blank'}
                              rel="noreferrer"
                            >
                              {item.title}
                            </FeLink>
                          </h3>
                          <p className="mb-4 text-gray-600">
                            {truncateText(removeTags(item.subtitle), 75)}
                          </p>
                          <FeLink
                            href={linkToPage}
                            title={item.title}
                            target="_blank"
                            className="flex w-full items-center justify-center rounded-xl border border-primary py-2 transition text-primary hover:bg-primary-dark hover:text-white"
                            onClick={() =>
                              pushDataLayer({
                                event: 'navigate',
                                component_type: 'article-list',
                                component_title: `${title}_${item.title}`,
                                action: 'click',
                                index,
                              })
                            }
                            rel="noreferrer"
                          >
                            <span>{tr('read_more', lang)}</span>
                            <MaterialIcon
                              className="ml-1"
                              icon="arrow_forward"
                              px={18}
                            />
                          </FeLink>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </PageContentContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
